const explicitApiUrl = (import.meta.env.VITE_API_URL || "").trim();
const defaultApiUrl = import.meta.env.DEV ? "http://localhost:5050" : "https://inqmise-api-djgwfrdpdgh6hmht.centralindia-01.azurewebsites.net";
export const API_URL = (explicitApiUrl || defaultApiUrl).replace(/\/$/, "");

// Media URLs are absolute (Azure Blob Storage) since the media/storage
// migration, but older uploads saved before that still have a relative
// `/uploads/...` path — only those need the API origin prefixed.
export const mediaUrl = (url) => (!url || /^https?:\/\//i.test(url) ? url : `${API_URL}${url}`);

async function request(path, options = {}) {
  const res = await fetch(`${API_URL}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error(data?.error || "Request failed");
  }
  return data;
}

// Separate from `request` because the browser must set its own
// multipart/form-data boundary header — passing Content-Type explicitly
// (as `request` does for JSON) breaks the upload.
async function uploadRequest(path, formData) {
  const res = await fetch(`${API_URL}${path}`, { method: "POST", credentials: "include", body: formData });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error(data?.error || "Upload failed");
  }
  return data;
}

export const getPosts = () => request("/api/posts");
export const getAllPosts = () => request("/api/posts/all");
export const getPost = (slug) => request(`/api/posts/${slug}`);
export const setPostReaction = (slug, previous, next) =>
  request(`/api/posts/${slug}/reaction`, { method: "PATCH", body: JSON.stringify({ previous, next }) });
export const createPost = (post) => request("/api/posts", { method: "POST", body: JSON.stringify(post) });
export const updatePost = (id, post) => request(`/api/posts/${id}`, { method: "PUT", body: JSON.stringify(post) });
export const deletePost = (id) => request(`/api/posts/${id}`, { method: "DELETE" });
export const setPostStatus = (id, status, scheduledFor) =>
  request(`/api/posts/${id}/status`, { method: "PATCH", body: JSON.stringify({ status, scheduledFor }) });
export const approvePost = (id) => request(`/api/posts/${id}/approve`, { method: "POST" });
export const publishNow = (id) => request(`/api/posts/${id}/publish-now`, { method: "POST" });
export const createPreviewToken = (id) => request(`/api/posts/${id}/preview-token`, { method: "POST" });
export const revokePreviewToken = (tokenId) => request(`/api/posts/preview-tokens/${tokenId}`, { method: "DELETE" });
export const getPreviewPost = (token) => request(`/api/posts/preview/${token}`);

export const getAuditLog = (params = {}) => {
  const search = new URLSearchParams(params).toString();
  return request(`/api/audit-log${search ? `?${search}` : ""}`);
};

export const adminLogin = (email, password) =>
  request("/api/admin/login", { method: "POST", body: JSON.stringify({ email, password }) });
export const adminLogout = () => request("/api/admin/logout", { method: "POST" });
export const getAdminSession = () => request("/api/admin/me");

export const getCategories = () => request("/api/categories");
export const createCategory = (name) => request("/api/categories", { method: "POST", body: JSON.stringify({ name }) });
export const deleteCategory = (id) => request(`/api/categories/${id}`, { method: "DELETE" });

export const getPostComments = (slug) => request(`/api/posts/${slug}/comments`);
export const submitComment = (slug, comment) =>
  request(`/api/posts/${slug}/comments`, { method: "POST", body: JSON.stringify(comment) });
export const setCommentReaction = (commentId, previous, next) =>
  request(`/api/comments/${commentId}/reaction`, { method: "PATCH", body: JSON.stringify({ previous, next }) });
export const submitTeamReply = (slug, { body, parentId }) =>
  request(`/api/posts/${slug}/comments/team-reply`, { method: "POST", body: JSON.stringify({ body, parentId }) });
export const getAllComments = (postId) => request(`/api/comments${postId ? `?post=${postId}` : ""}`);
export const deleteComment = (id) => request(`/api/comments/${id}`, { method: "DELETE" });

export const getMedia = () => request("/api/media");
export const deleteMedia = (id) => request(`/api/media/${id}`, { method: "DELETE" });
export const uploadMedia = (file) => {
  const formData = new FormData();
  formData.append("file", file);
  return uploadRequest("/api/media", formData);
};

export const recordView = (slug, visitorId, sessionId) =>
  request("/api/analytics/view", { method: "POST", body: JSON.stringify({ slug, visitorId, sessionId }) });
export const pingReadTime = (slug, visitorId, sessionId, seconds) =>
  request("/api/analytics/ping", { method: "POST", body: JSON.stringify({ slug, visitorId, sessionId, seconds }) });
// range: "24h" | "7d" | "30d" | "90d" | "all"/omitted (all-time, today's
// default behavior — nothing breaks if a call site never passes one).
const withRange = (path, range) => (range && range !== "all" ? `${path}?range=${range}` : path);
export const getAnalyticsSummary = (range) => request(withRange("/api/analytics/summary", range));
export const getTopPosts = (range) => request(withRange("/api/analytics/top-posts", range));
export const getPostAnalytics = (slug, range) => request(withRange(`/api/analytics/post/${slug}`, range));
export const getRecentViews = (days) => request(`/api/analytics/recent-views${days ? `?days=${days}` : ""}`);

export const getUsers = () => request("/api/users");
export const createUser = (user) => request("/api/users", { method: "POST", body: JSON.stringify(user) });
export const deleteUser = (id) => request(`/api/users/${id}`, { method: "DELETE" });

export const getLinkedInPosts = () => request("/api/linkedin-posts");
export const retryLinkedInPost = (id) => request(`/api/linkedin-posts/${id}/retry`, { method: "POST" });

export const getAiDraftJobsSummary = (range) => request(withRange("/api/ai-draft-jobs/summary", range));
